import axios from "axios";
import { plog } from "../utils/perfLog";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Several independent components on the same page (Home's Hero, SecondIntro,
 * Highlight, Treatments, ExpertCare, Footer, Navbar, ...) each independently
 * fetch the same read-only `/content?page=X&lang=Y` CMS payload on mount —
 * without this, that's N identical network round-trips for one page load.
 * Cache successful GET responses by resolved URL for the session, and share
 * the in-flight promise across concurrent requests to the same URL so they
 * resolve together instead of each firing their own request. Admin content
 * editing goes through a separate axios instance (see api/adminContent.js),
 * so cached staleness here never affects the CMS editing flow.
 */
const responseCache = new Map();
const inFlightRequests = new Map();

const originalGet = api.get.bind(api);

api.get = (url, config) => {
  const cacheKey = api.getUri({ url, ...config });

  if (responseCache.has(cacheKey)) {
    plog("net", `CACHE HIT   ${cacheKey}`);
    return Promise.resolve(responseCache.get(cacheKey));
  }

  if (inFlightRequests.has(cacheKey)) {
    plog("net", `DEDUPED     ${cacheKey} (joined in-flight request)`);
    return inFlightRequests.get(cacheKey);
  }

  plog("net", `→ REQUEST   ${cacheKey}`);
  const startedAt = performance.now();

  const request = originalGet(url, config)
    .then((response) => {
      plog(
        "net",
        `← RESPONSE  ${cacheKey} in ${Math.round(performance.now() - startedAt)}ms`
      );
      responseCache.set(cacheKey, response);
      return response;
    })
    .catch((err) => {
      plog("blank", `✕ FAILED    ${cacheKey}: ${err.message}`);
      throw err;
    })
    .finally(() => {
      inFlightRequests.delete(cacheKey);
    });

  inFlightRequests.set(cacheKey, request);
  return request;
};
